/**
 * RedemptionMeter.js - VERSÃO PREMIUM
 * 
 * Medidor visual épico de progresso para promoção
 * Design de game com níveis, partículas e animações
 */

import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing, Dimensions, Platform } from 'react-native';
import { Text } from '../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path, Circle, Defs, LinearGradient as SvgGradient, Stop, G } from 'react-native-svg';
import { SHORT_TERM_COLORS, SHORT_TERM_MEMORY_CONFIG } from '../../constants/categories';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const RedemptionMeter = ({
    consecutiveCorrect = 0,
    compact = false,
    showLabel = true,
    showParticles = true,
    animated = true,
}) => {
    const total = SHORT_TERM_MEMORY_CONFIG.CONSECUTIVE_CORRECT_TO_PROMOTE;
    const remaining = total - consecutiveCorrect;
    const progress = (consecutiveCorrect / total) * 100;

    // Animações
    const progressAnim = useRef(new Animated.Value(0)).current;
    const glowAnim = useRef(new Animated.Value(0)).current;
    const bounceAnim = useRef(new Animated.Value(0)).current;
    const particle1 = useRef(new Animated.Value(0)).current;
    const particle2 = useRef(new Animated.Value(0)).current;
    const particle3 = useRef(new Animated.Value(0)).current;

    // Animar progresso
    useEffect(() => {
        if (animated) {
            Animated.spring(progressAnim, {
                toValue: progress,
                tension: 50,
                friction: 8,
                useNativeDriver: false,
            }).start();
        } else {
            progressAnim.setValue(progress);
        }
    }, [progress, animated, progressAnim]);

    // Glow animation
    useEffect(() => {
        const glow = Animated.loop(
            Animated.sequence([
                Animated.timing(glowAnim, { toValue: 1, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
                Animated.timing(glowAnim, { toValue: 0.5, duration: 1500, useNativeDriver: Platform.OS !== 'web' }),
            ])
        );
        glow.start();
        return () => glow.stop();
    }, [glowAnim]);

    // Bounce quando há progresso
    useEffect(() => {
        if (consecutiveCorrect > 0) {
            Animated.sequence([
                Animated.timing(bounceAnim, { toValue: -10, duration: 150, useNativeDriver: true }),
                Animated.spring(bounceAnim, { toValue: 0, tension: 200, friction: 5, useNativeDriver: true }),
            ]).start();
        }
    }, [consecutiveCorrect, bounceAnim]);

    // Partículas flutuantes
    useEffect(() => {
        if (showParticles && consecutiveCorrect > 0) {
            const animateParticle = (anim, delay) => {
                Animated.loop(
                    Animated.sequence([
                        Animated.delay(delay),
                        Animated.timing(anim, { toValue: 1, duration: 2000, useNativeDriver: Platform.OS !== 'web' }),
                        Animated.timing(anim, { toValue: 0, duration: 0, useNativeDriver: Platform.OS !== 'web' }),
                    ])
                ).start();
            };
            animateParticle(particle1, 0);
            animateParticle(particle2, 700);
            animateParticle(particle3, 1400);
        }
    }, [showParticles, consecutiveCorrect, particle1, particle2, particle3]);

    // Labels para cada estágio
    const getStatusInfo = () => {
        if (consecutiveCorrect === 0) return {
            label: '🎯 Comece a praticar!',
            sublabel: 'Acerte 3 vezes consecutivas',
            color: '#FF6B6B',
            emoji: '😤',
        };
        if (consecutiveCorrect === 1) return {
            label: '🔥 Bom começo!',
            sublabel: 'Mais 2 acertos para promover',
            color: '#FF9F43',
            emoji: '💪',
        };
        if (consecutiveCorrect === 2) return {
            label: '⚡ Quase lá!',
            sublabel: 'Só mais 1 acerto!',
            color: '#51CF66',
            emoji: '🚀',
        };
        return {
            label: '🏆 Pronto para promover!',
            sublabel: 'Parabéns!',
            color: '#FFD700',
            emoji: '🎉',
        };
    };

    const statusInfo = getStatusInfo();

    // Cores do progresso
    const getProgressGradient = () => {
        if (consecutiveCorrect === 0) return ['#FF6B6B', '#FF8E8E'];
        if (consecutiveCorrect === 1) return ['#FF9F43', '#FFBE76'];
        if (consecutiveCorrect === 2) return ['#51CF66', '#7ED957'];
        return ['#FFD700', '#FFA500'];
    };

    if (compact) {
        return (
            <View style={styles.compactContainer}>
                <View style={styles.compactDots}>
                    {[...Array(total)].map((_, index) => (
                        <View
                            key={index}
                            style={[
                                styles.compactDot,
                                index < consecutiveCorrect && styles.compactDotFilled,
                            ]}
                        >
                            {index < consecutiveCorrect && <Text style={styles.compactCheck}>✓</Text>}
                        </View>
                    ))}
                </View>
                <Text style={[styles.compactText, { color: statusInfo.color }]}>
                    {consecutiveCorrect}/{total}
                </Text>
            </View>
        );
    }

    return (
        <Animated.View style={[styles.container, { transform: [{ translateY: bounceAnim }] }]}>
            {/* Título épico */}
            <View style={styles.titleRow}>
                <Text style={styles.emoji}>{statusInfo.emoji}</Text>
                <View style={styles.titleContent}>
                    <Text style={styles.title}>Jornada de Recuperação</Text>
                    <Text style={[styles.subtitle, { color: statusInfo.color }]}>{statusInfo.label}</Text>
                </View>
            </View>

            {/* Círculos de progresso estilo game */}
            <View style={styles.stepsContainer}>
                {[...Array(total)].map((_, index) => {
                    const isCompleted = index < consecutiveCorrect;
                    const isCurrent = index === consecutiveCorrect;
                    const isLast = index === total - 1;

                    return (
                        <View key={index} style={styles.stepWrapper}>
                            {/* Conector */}
                            {index > 0 && (
                                <View style={[
                                    styles.connector,
                                    isCompleted && styles.connectorCompleted,
                                ]}>
                                    <LinearGradient
                                        colors={isCompleted ? ['#51CF66', '#7ED957'] : ['#2A2D3A', '#2A2D3A']}
                                        style={styles.connectorGradient}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 0 }}
                                    />
                                </View>
                            )}

                            {/* Círculo do passo */}
                            <Animated.View
                                style={[
                                    styles.stepCircle,
                                    isCompleted && styles.stepCompleted,
                                    isCurrent && styles.stepCurrent,
                                    isCurrent && {
                                        transform: [{
                                            scale: glowAnim.interpolate({
                                                inputRange: [0.5, 1],
                                                outputRange: [1, 1.1],
                                            })
                                        }]
                                    },
                                ]}
                            >
                                {isCompleted ? (
                                    <LinearGradient
                                        colors={['#51CF66', '#7ED957']}
                                        style={styles.stepInner}
                                    >
                                        <Text style={styles.checkmark}>✓</Text>
                                    </LinearGradient>
                                ) : isLast ? (
                                    <View style={styles.stepInnerTrophy}>
                                        <Text style={styles.trophyIcon}>🏆</Text>
                                    </View>
                                ) : (
                                    <View style={[styles.stepInner, styles.stepInnerEmpty]}>
                                        <Text style={[styles.stepNumber, isCurrent && styles.stepNumberCurrent]}>
                                            {index + 1}
                                        </Text>
                                    </View>
                                )}

                                {/* Glow ring para current */}
                                {isCurrent && (
                                    <Animated.View
                                        style={[
                                            styles.glowRing,
                                            { opacity: glowAnim }
                                        ]}
                                    />
                                )}
                            </Animated.View>

                            {/* Label do passo */}
                            <Text style={[
                                styles.stepLabel,
                                isCompleted && styles.stepLabelCompleted,
                                isCurrent && styles.stepLabelCurrent,
                            ]}>
                                {isLast ? 'Promover!' : `${index + 1}º Acerto`}
                            </Text>
                        </View>
                    );
                })}
            </View>

            {/* Barra de progresso épica */}
            <View style={styles.progressSection}>
                <View style={styles.progressBackground}>
                    {/* Brilhos decorativos */}
                    <View style={styles.progressShine} />

                    {/* Barra de progresso */}
                    <Animated.View
                        style={[
                            styles.progressFill,
                            {
                                width: progressAnim.interpolate({
                                    inputRange: [0, 100],
                                    outputRange: ['0%', '100%'],
                                })
                            }
                        ]}
                    >
                        <LinearGradient
                            colors={getProgressGradient()}
                            style={styles.progressGradient}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                        >
                            {/* Partículas na barra */}
                            {showParticles && consecutiveCorrect > 0 && (
                                <>
                                    <Animated.View style={[styles.particle, {
                                        opacity: particle1,
                                        transform: [{
                                            translateY: particle1.interpolate({
                                                inputRange: [0, 1],
                                                outputRange: [0, -20],
                                            })
                                        }],
                                    }]}>
                                        <Text style={styles.particleText}>✨</Text>
                                    </Animated.View>
                                </>
                            )}
                        </LinearGradient>
                    </Animated.View>
                </View>

                {/* Porcentagem */}
                <View style={styles.percentContainer}>
                    <Text style={[styles.percentText, { color: statusInfo.color }]}>
                        {Math.round(progress)}%
                    </Text>
                </View>
            </View>

            {/* Mensagem motivacional */}
            {showLabel && (
                <View style={[styles.messageContainer, { backgroundColor: statusInfo.color + '20' }]}>
                    <Text style={[styles.messageText, { color: statusInfo.color }]}>
                        {statusInfo.sublabel}
                    </Text>
                </View>
            )}
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 20,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: 24,
        marginVertical: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 24,
    },
    emoji: {
        fontSize: 36,
        marginRight: 12,
    },
    titleContent: {
        flex: 1,
    },
    title: {
        fontSize: 18,
        fontWeight: '800',
        color: '#E8EAED',
        letterSpacing: -0.3,
    },
    subtitle: {
        fontSize: 14,
        fontWeight: '600',
        marginTop: 2,
    },
    stepsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        marginBottom: 24,
        paddingHorizontal: 10,
    },
    stepWrapper: {
        alignItems: 'center',
        flex: 1,
        position: 'relative',
    },
    connector: {
        position: 'absolute',
        left: -25,
        top: 24,
        width: 50,
        height: 4,
        zIndex: -1,
    },
    connectorGradient: {
        flex: 1,
        borderRadius: 2,
    },
    stepCircle: {
        width: 52,
        height: 52,
        borderRadius: 26,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: 3,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    stepCompleted: {
        borderColor: '#51CF66',
    },
    stepCurrent: {
        borderColor: '#FF9F43',
        borderWidth: 3,
    },
    stepInner: {
        width: 42,
        height: 42,
        borderRadius: 21,
        justifyContent: 'center',
        alignItems: 'center',
    },
    stepInnerEmpty: {
        backgroundColor: 'rgba(255,255,255,0.05)',
    },
    stepInnerTrophy: {
        width: 42,
        height: 42,
        borderRadius: 21,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 215, 0, 0.2)',
    },
    checkmark: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '800',
    },
    trophyIcon: {
        fontSize: 24,
    },
    stepNumber: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 18,
        fontWeight: '700',
    },
    stepNumberCurrent: {
        color: '#FF9F43',
    },
    stepLabel: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
        marginTop: 8,
        fontWeight: '600',
        textAlign: 'center',
    },
    stepLabelCompleted: {
        color: '#51CF66',
    },
    stepLabelCurrent: {
        color: '#FF9F43',
    },
    glowRing: {
        position: 'absolute',
        width: 64,
        height: 64,
        borderRadius: 32,
        borderWidth: 2,
        borderColor: '#FF9F43',
    },
    progressSection: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    progressBackground: {
        flex: 1,
        height: 12,
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderRadius: 6,
        overflow: 'hidden',
        position: 'relative',
    },
    progressShine: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 2,
    },
    progressFill: {
        height: '100%',
        borderRadius: 6,
        overflow: 'hidden',
    },
    progressGradient: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    particle: {
        position: 'absolute',
        right: 10,
    },
    particleText: {
        fontSize: 12,
    },
    percentContainer: {
        marginLeft: 12,
        minWidth: 48,
    },
    percentText: {
        fontSize: 16,
        fontWeight: '800',
    },
    messageContainer: {
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 12,
        alignItems: 'center',
    },
    messageText: {
        fontSize: 13,
        fontWeight: '700',
        textAlign: 'center',
    },
    // Compact styles
    compactContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    compactDots: {
        flexDirection: 'row',
        gap: 6,
    },
    compactDot: {
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: 'rgba(255,255,255,0.1)',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
    },
    compactDotFilled: {
        backgroundColor: '#51CF66',
        borderColor: '#51CF66',
    },
    compactCheck: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '700',
    },
    compactText: {
        marginLeft: 8,
        fontSize: 13,
        fontWeight: '700',
    },
});

export default RedemptionMeter;
